// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ShoppingListResponse } from "@bitebalance/contracts"

export type VisiblePricing = NonNullable<ShoppingListResponse["pricing"]>

/**
 * The pricing summary of the VISIBLE shopping rows — the only subtotal the
 * Shop page may headline (owner invariant 2026-09-04: one dataset per
 * presentation). The server sends it as `list.pricing`; a server that does
 * not yet send the block degrades to the SAME summary computed from the
 * rows themselves, by the contract's own definition: a required row is a
 * manual row or a calculated row the user must buy (buy > 0), and it is
 * priced when it carries a usable price estimate (C-043: an unavailable
 * price is skipped, never counted as €0).
 */
export function visiblePricing(list: Pick<ShoppingListResponse, "items" | "pricing">): VisiblePricing {
  if (list.pricing) return list.pricing
  let pricedSubtotalCents = 0
  let pricedRowCount = 0
  let unpricedRequiredCount = 0
  let realPriceCount = 0
  let estimatedPriceCount = 0
  for (const item of list.items) {
    const price = item.priceEstimate
    if (price !== null && price.status !== "unavailable") {
      pricedSubtotalCents += price.amountCents
      pricedRowCount += 1
      if (price.status === "cached") realPriceCount += 1
      else if (price.status === "estimated") estimatedPriceCount += 1
      continue
    }
    const required = item.manual || item.buyQuantity === null || item.buyQuantity > 0
    if (required) unpricedRequiredCount += 1
  }
  return {
    complete: unpricedRequiredCount === 0,
    pricedSubtotalCents,
    pricedRowCount,
    unpricedRequiredCount,
    realPriceCount,
    estimatedPriceCount,
  }
}
